import { inject, Injectable, OnDestroy } from '@angular/core';
import { NotificationService } from '../notification.service';
import { TranslateService } from '@ngx-translate/core';
import { WebSocketResponse } from '@models/websocket-types';

@Injectable({
  providedIn: 'root'
})
export class WebSocketErrorHandlerService implements OnDestroy {
  private processedErrorMessages = new Map<string, number>();
  private readonly ERROR_MESSAGE_TTL = 60000;
  private cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);

  public handleErrorMessage(data: WebSocketResponse): boolean {
    const errorMessage = data.error?.message || data.message || 'Unknown error';
    const deduplicationKey = this.createDeduplicationKey(data);
    const currentTime = Date.now();

    if (this.isDuplicateError(deduplicationKey, currentTime)) {
      return false;
    }

    this.processedErrorMessages.set(deduplicationKey, currentTime);
    if (!this.cleanupIntervalId) this.startErrorMessageCleanup();

    this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.INTEGRATION_ERROR', { error: errorMessage }));
    if (data.error?.stack) console.error('Error details:', data.error);

    return true;
  }

  private createDeduplicationKey(data: WebSocketResponse): string {
    const errorMsg = data.error?.message || data.message || '';
    const source = data.source || '';
    const type = data.type || '';
    const entityId = this.extractEntityId(data);
    return `${source}:${type}:${errorMsg}:${entityId}`;
  }

  private extractEntityId(data: WebSocketResponse): string {
    if (Array.isArray(data.data) && data.data.length > 0) {
      const firstItem = data.data[0];
      if (firstItem && typeof firstItem === 'object') {
        return String((firstItem as Record<string, unknown>)['id'] || '');
      }
    }
    return '';
  }

  private isDuplicateError(key: string, currentTime: number): boolean {
    const lastProcessedTime = this.processedErrorMessages.get(key);
    return lastProcessedTime !== undefined && currentTime - lastProcessedTime < this.ERROR_MESSAGE_TTL;
  }

  private cleanupExpiredErrorMessages(): void {
    const currentTime = Date.now();
    this.processedErrorMessages.forEach((timestamp, key) => {
      if (currentTime - timestamp > this.ERROR_MESSAGE_TTL) {
        this.processedErrorMessages.delete(key);
      }
    });
  }

  private startErrorMessageCleanup(): void {
    this.cleanupExpiredErrorMessages();
    this.cleanupIntervalId = setInterval(() => this.cleanupExpiredErrorMessages(), 60000);
  }

  private stopErrorMessageCleanup(): void {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }
  }

  ngOnDestroy(): void {
    this.stopErrorMessageCleanup();
  }
}
